import { saveDefaultsToSource, type SaveDefaultsResult } from '../dev/saveDefaults';
import defaults from './StyleConfig.defaults.json';
import {
  BINDINGS_FILE,
  MAP_PRESETS,
  TRACK_PRESET_BINDINGS,
  isValidPresetName,
  presetFilePath,
  presetForName,
  presetForTrack,
  type MapPresetMeta,
} from './presets';
import { assemblyToStyleConfig, loadAssemblyForTrack } from '../scene';

export type ToneMappingChoice = 'None' | 'ACES' | 'AgX' | 'Linear';

export type CloudLayerName = 'low' | 'mid' | 'high';
export type CloudShape = 'ribbon' | 'wisp' | 'puff' | 'bank';
export type ArtDirectionPreset =
  | 'desertHero'
  | 'hotDesertNoon'
  | 'electricSunset'
  | 'purpleDusk'
  | 'blueMoonNight'
  | 'neonStorm';

export interface MasterLightingConfig {
  sunElevationDeg: number;
  sunAzimuthDeg: number;
  secondarySun: {
    enabled: boolean;
    relativeElevationDeg: number;
    relativeAzimuthDeg: number;
    intensity: number;
  };
  atmosphere: {
    turbidity: number;
    hazeBoost: number;
    warmthBias: number;
  };
}

export interface StyleOverride {
  enabled: boolean;
  value: unknown;
}

export type StyleOverrideMap = Record<string, StyleOverride>;

export interface DayCycleKeyframe {
  id: string;
  name: string;
  sunElevationDeg: number;
  master: MasterLightingConfig;
  overrides: StyleOverrideMap;
}

export interface DayCycleConfig {
  enabled: boolean;
  scrub: number;
  playing: boolean;
  durationSec: number;
  keyframes: DayCycleKeyframe[];
}

export interface CloudLayerConfig {
  enabled: boolean;
  count: number;
  /** Base Y position in world units. Layer body extends upward by ~135 units. */
  altitude: number;
  /** Lateral spread of the dome ring (world units). */
  domeRadius: number;
  /** 0..1, probability of an extra wisp mesh per cloud — adds chunk/density. */
  coverage: number;
  /** Per-layer global scale multiplier on cloud width/height. */
  scale: number;
  /** Painterly silhouette mode for this cloud layer. */
  shape: CloudShape;
  /** Base card width in world units before elongation and scale. */
  width: number;
  /** Base card height in world units before scale. */
  height: number;
  /** Horizontal stretch multiplier for long sky streaks. */
  elongation: number;
  /** 0..1 alpha falloff at the cloud edge. */
  edgeSoftness: number;
  /** Procedural breakup frequency in card UV space. */
  noiseScale: number;
  /** 0..1 amount of edge breakup and internal fade. */
  noiseStrength: number;
  /** Layer opacity after procedural alpha. */
  opacity: number;
  /** 0..1 fake normal strength for lit-side/shadow-side modeling. */
  normalStrength: number;
  /** 0..1 centerline wave/curl for painterly streaks. */
  curl: number;
  /** 0..1 per-cloud upper-edge drift for wispy sheared silhouettes. */
  windShear: number;
  /** Whether scene fog tints this cloud layer. */
  fogEnabled: boolean;
  /** Rotation speed (rad/s). Per-layer so far clouds can drift slower → parallax. */
  rotateSpeed: number;
}

export interface CloudShadingConfig {
  cloudBaseHex: string;
  wrap: number;
  rimStrength: number;
}

export interface StyleConfig {
  master: MasterLightingConfig;
  overrides: StyleOverrideMap;
  dayCycle: DayCycleConfig;
  artDirection: {
    preset: ArtDirectionPreset;
    drama: number;
    warmth: number;
    saturation: number;
    contrast: number;
    hazeGlow: number;
    valueComposition: {
      fgExposure: number;
      mgExposure: number;
      bgExposure: number;
    };
  };
  sky: {
    horizonHex: string;
    lowerHex: string;
    midHex: string;
    upperHex: string;
    zenithHex: string;
    oppositeHorizonHex: string;
    oppositeHorizonStrength: number;
    horizonGlowHex: string;
    horizonGlowIntensity: number;
    sunDiscScale: number;
    sunHaloScale: number;
    secondarySunDiscScale: number;
    secondarySunHaloScale: number;
    starIntensity: number;
    sunDir: { x: number; y: number; z: number };
    secondarySunDir: { x: number; y: number; z: number };
    binary: {
      centerElevation: number;
      centerAzimuth: number;
      separationDeg: number;
      tiltDeg: number;
      distance: number;
    };
    panoramaUrl: string | null;
  };
  sunRig: {
    key: {
      colorHex: string;
      intensity: number;
      minElevationDeg: number;
      elevationOffsetDeg: number;
      azimuthOffsetDeg: number;
      castShadow: boolean;
    };
    fill: {
      colorHex: string;
      intensity: number;
      minElevationDeg: number;
    };
    hemisphere: {
      skyColorHex: string;
      groundColorHex: string;
      intensity: number;
    };
    bounce: {
      colorHex: string;
      intensity: number;
      elevationDeg: number;
      azimuthDeg: number;
    };
    rim: {
      colorHex: string;
      intensity: number;
      elevationDeg: number;
      azimuthDeg: number;
      cameraAnchored: boolean;
      cameraSpaceDir: { x: number; y: number; z: number };
    };
    /** Scene.environment contribution for PBR materials, separate from sun/fog. */
    environmentIntensity: number;
  };
  clouds: {
    shading: CloudShadingConfig;
    layers: {
      low: CloudLayerConfig;
      mid: CloudLayerConfig;
      high: CloudLayerConfig;
    };
  };
  asphalt: {
    baseHex: string;
    baseRoughness: number;
    baseEnvIntensity: number;
    aggregateColorHex: string;
    aggregateIntensity: number;
    aggregateScale: number;
    /** Metres covered by one cycle of the grit texture. Larger = grain stretched
     *  over more road; smaller = tighter, more visibly repeating. */
    grainTileMeters: number;
    /** 0..1 mix of an offset/rescaled second sample of the same texture. Higher
     *  values break the visible 6 m repeat at the cost of contrast. */
    grainDetileMix: number;
    /** Y-vs-X cell elongation in the procedural noise. 1.0 = isotropic; >1
     *  produces along-track fiber bias; <1 produces lateral bias. */
    grainElongation: number;
    rubberStreakHex: string;
    rubberStreakIntensity: number;
    /** Lower = longer streaks (multiplier on longitudinal UV before fbm). */
    rubberStreakLongScale: number;
    rubberStreakLateralFreq: number;
    rubberStreakLaneWidth: number;
    rubberStreakThreshold: number;
    oilSheenHex: string;
    oilSheenIntensity: number;
    oilSheenLongScale: number;
    oilSheenThreshold: number;
    centerWear: number;
    edgeDebris: number;
    /** WebGPU-only multiplier applied on top of `aggregateIntensity` inside
     *  `applyWebGPUAsphaltStyle`. WebGL ignores this. Default 1.0 = parity.
     *  Use this to dial WebGPU's aggregate contrast independently when the
     *  TSL composition reads differently from the WebGL onBeforeCompile path
     *  (this happens at distance / under different anisotropic sampling). */
    webgpuAggregateMultiplier: number;
    cracks: {
      colorHex: string;
      intensity: number;
      fineChecking: number;
      majorCount: number;
      branchiness: number;
      transverseBias: number;
      cornerBias: number;
      edgeBias: number;
      maxLength: number;
      width: number;
      depth: number;
      seed: number;
    };
  };
  trackGlint: {
    colorHex: string;
    intensity: number;
    exponent: number;
    streakScale: number;
    broadness: number;
    textureInfluence: number;
    normalStrength: number;
    roughnessContrast: number;
    /** WebGPU-only multiplier applied on top of `normalStrength` inside
     *  `applyWebGPUAsphaltStyle`. WebGL ignores this. Default 1.0 = parity.
     *  Lets you tune WebGPU bump amplitude without disturbing the proven
     *  WebGL value. The WebGL path uses its own per-fragment derivation. */
    webgpuBumpMultiplier: number;
  };
  edgeLines: {
    colorHex: string;
    width: number;
    emissiveStrength: number;
    /** Deprecated: edge paint is integrated into the asphalt shader. */
    heightOffset: number;
  };
  kerbs: {
    enabled: boolean;
    continuous: boolean;
    baseWidth: number;
    cornerWidthBoost: number;
    stripeLength: number;
    redHex: string;
    whiteHex: string;
    height: number;
    ridgeStrength: number;
    rubberInfluence: number;
    roughness: number;
    metalness: number;
    envMapIntensity: number;
    emissiveHex: string;
    emissiveIntensity: number;
  };
  furniture: {
    enabled: boolean;
    chevronsEnabled: boolean;
    railsEnabled: boolean;
    bannersEnabled: boolean;
    bannerGlbUrl: string | null;
    spacing: number;
    cornerDensity: number;
  };
  fog: {
    /** Hex color for the haze. Tints distance toward this color. */
    colorHex: string;
    /** FogExp2 density in track-scale world units. ~0.0002 = light aerial haze, ~0.002 = heavy stylized haze. */
    density: number;
    /** Match the fog tint to the sky horizon every frame. When true, colorHex is overridden by the live sky horizon. */
    followSky: boolean;
    /** 0..1, warm forward-scatter strength when looking toward the sun. */
    inscatterStrength: number;
    /** 1..16, sharpness of the warm sun-facing fog halo. */
    inscatterPower: number;
    warmTintHex: string;
    coolTintHex: string;
  };
  /** Desert sand surface around the track. Drives the terrain palette and the
   *  procedural shader (wind ripples, surface speckle, dune-crest glint). */
  ground: {
    shadowHex: string;
    valleyHex: string;
    sandHex: string;
    ridgeHex: string;
    peakHex: string;
    /** 0..2, multiplies chroma in the fragment shader. 1 = unchanged. */
    saturationBoost: number;
    /** 0..1, anisotropic ripple normal-map strength. */
    rippleIntensity: number;
    /** Repeat frequency along the wind axis (high = tighter ripples). */
    rippleScaleU: number;
    /** Repeat frequency across the wind axis (low = elongated ripples). */
    rippleScaleV: number;
    /** Wind direction in degrees, rotates ripple UV before sampling. */
    rippleAngleDeg: number;
    /** 0..0.5 darkening strength for sparse grain speckle. */
    speckleIntensity: number;
    /** 0..1 hash threshold; higher = sparser. */
    speckleDensity: number;
    /** 0..0.4 low-freq albedo wobble strength (dust patches). */
    patchIntensity: number;
    /** Per-meter scale for the patch fbm. ~0.05. */
    patchScale: number;
    /** 0..1 sun-grazing dune-crest sheen contribution. */
    glintIntensity: number;
    /** Specular tightness for the glint term. */
    glintExponent: number;
    glintColorHex: string;
  };
  sfx: {
    bloom: {
      threshold: number;
      radius: number;
      intensity: number;
    };
    selectiveBloom: {
      enabled: boolean;
      intensity: number;
    };
    colorGrade: {
      toneMapping: ToneMappingChoice;
      exposure: number;
      enabled: boolean;
      lift: { r: number; g: number; b: number };
      gain: { r: number; g: number; b: number };
      saturation: number;
      contrast: number;
      gamma: number;
      lutUrl: string | null;
    };
    vignette: {
      strength: number;
      softness: number;
      tintHex: string;
    };
    chroma: {
      amount: number;
      falloff: number;
      asymmetry: number;
    };
    motion: {
      enabled: boolean;
      /** Upper bound on blur magnitude when at full speed. */
      radialStrength: number;
      radialSamples: 4 | 8 | 16;
      /** When true, drive blur from real camera velocity. When false, preview via fakeSpeed only. */
      autoFromCamera: boolean;
      /** Speed (m/s) at which blur reaches `radialStrength`. */
      fullSpeed: number;
      /** Manual preview slider (0..1.4). Used when autoFromCamera = false, OR added on top of camera-driven strength. */
      fakeSpeed: number;
      /** Power curve on the radial distance term. 1 = linear, 2+ = sharper focal point. */
      falloff: number;
      /** Per-channel UV offset multiplier for speed-streak color split (0..0.02). */
      chromaticAmount: number;
      pavementMaskOnly: boolean;
    };
  };
}

/** Bumped to v7 for unified SFX state. */
export const STYLE_CONFIG_STORAGE_KEY = 'tst:style-config:v7';
export const STYLE_CONFIG_EVENT = 'style-config-change';
export const STYLE_CONFIG_DEFAULTS_FILE = 'src/render/StyleConfig.defaults.json' as const;

export const DEFAULT_STYLE_CONFIG: StyleConfig = defaults as unknown as StyleConfig;
const MASTER_SUN_DIRECTION_OVERRIDE_PATHS = new Set(['sky.sunDir', 'sky.secondarySunDir']);

export function cloneStyleConfig(config: StyleConfig = DEFAULT_STYLE_CONFIG): StyleConfig {
  return JSON.parse(JSON.stringify(config)) as StyleConfig;
}

export function hydrateStyleConfig(raw: unknown): StyleConfig {
  return mergeStyleConfig(cloneStyleConfig(), raw);
}

export function loadStyleConfig(): StyleConfig {
  if (typeof window === 'undefined') return cloneStyleConfig();
  try {
    const raw = window.localStorage.getItem(STYLE_CONFIG_STORAGE_KEY);
    if (!raw) return cloneStyleConfig();
    return hydrateStyleConfig(JSON.parse(raw));
  } catch {
    return cloneStyleConfig();
  }
}

export function saveStyleConfig(config: StyleConfig): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STYLE_CONFIG_STORAGE_KEY, JSON.stringify(config));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Wipe localStorage and return a fresh copy of the committed JSON defaults. */
export function resetStyleConfig(): StyleConfig {
  if (typeof window !== 'undefined') {
    try { window.localStorage.removeItem(STYLE_CONFIG_STORAGE_KEY); } catch { /* ignore */ }
  }
  return cloneStyleConfig();
}

/**
 * Persist the panel's current state to `StyleConfig.defaults.json` via the
 * dev-only Vite middleware. The file lives in source control, so reload after
 * save means the new values ship with the code. Returns ok:false in prod.
 */
export function saveStyleConfigToSource(config: StyleConfig): Promise<SaveDefaultsResult> {
  return saveDefaultsToSource(STYLE_CONFIG_DEFAULTS_FILE, config);
}

/** localStorage key for a per-preset dev overlay. Each preset gets its own
 *  scratchpad so switching presets in the dev panel doesn't bleed edits
 *  across them. */
function presetStorageKey(name: string): string {
  return `tst:style-preset:${name}:v1`;
}

export function loadPresetOverlay(name: string): StyleConfig | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(presetStorageKey(name));
    if (!raw) return null;
    return hydrateStyleConfig(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function savePresetOverlay(name: string, config: StyleConfig): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(presetStorageKey(name), JSON.stringify(config));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearPresetOverlay(name: string): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(presetStorageKey(name)); } catch { /* ignore */ }
}

export function hasPresetOverlay(name: string): boolean {
  if (typeof window === 'undefined') return false;
  try { return window.localStorage.getItem(presetStorageKey(name)) !== null; }
  catch { return false; }
}

export interface ResolvedPreset {
  name: string;
  label: string;
  trackUrl: string;
  /** Source preset on disk — what production will load. Useful for diffing. */
  saved: StyleConfig;
  /** Live config — overlay if dev session has unsaved tweaks, otherwise === saved. */
  config: StyleConfig;
  hasOverlay: boolean;
  /** True when the track URL had no preset binding and we fell back to the
   *  global StyleConfig.defaults.json. Save in this state would be ambiguous. */
  isFallback: boolean;
}

/**
 * Looks up the preset bound to `trackUrl` and returns both the saved source
 * preset and the live dev config. Production-like callers must use `saved`;
 * editor callers can use `config` to include any per-browser scratch overlay.
 *
 * Falls back to the legacy `StyleConfig.defaults.json` (and its legacy
 * localStorage key) when no preset is bound — keeps behaviour unchanged for
 * any track URL that hasn't been migrated yet.
 */
export function loadStyleForTrack(trackUrl: string): ResolvedPreset {
  if (import.meta.env.VITE_SCENE_LAYERS === '1') {
    const assembly = loadAssemblyForTrack(trackUrl);
    const saved = hydrateStyleConfig(assemblyToStyleConfig(assembly));
    return {
      name: assembly.id,
      label: labelFromId(assembly.id),
      trackUrl,
      saved,
      config: cloneStyleConfig(saved),
      hasOverlay: false,
      isFallback: false,
    };
  }

  const preset = presetForTrack(trackUrl);
  if (preset) {
    const overlay = loadPresetOverlay(preset.name);
    const saved = hydrateStyleConfig(preset.data);
    return {
      name: preset.name,
      label: preset.label,
      trackUrl,
      saved,
      config: overlay ?? cloneStyleConfig(saved),
      hasOverlay: overlay !== null,
      isFallback: false,
    };
  }
  const legacy = loadStyleConfig();
  return {
    name: '',
    label: '(unbound — defaults)',
    trackUrl,
    saved: cloneStyleConfig(),
    config: legacy,
    hasOverlay: false,
    isFallback: true,
  };
}

function labelFromId(id: string): string {
  return id
    .split('-')
    .map((part) => (part.length > 0 ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}

/** List of presets currently known to the registry, for the dev dropdown. */
export function listMapPresets(): MapPresetMeta[] {
  return MAP_PRESETS.slice();
}

/** Bind a track URL to a preset name on disk so production loads it next boot. */
export async function bindTrackToPreset(
  trackUrl: string,
  presetName: string,
): Promise<SaveDefaultsResult> {
  if (!isValidPresetName(presetName)) {
    return { ok: false, error: `invalid preset name: ${presetName}` };
  }
  const next = { ...TRACK_PRESET_BINDINGS, [trackUrl]: presetName };
  return saveDefaultsToSource(BINDINGS_FILE, next);
}

/** Overwrite an existing preset's JSON file. Caller must ensure the preset
 *  already exists in the registry; otherwise prefer createNewPreset. */
export async function saveStyleToPreset(
  presetName: string,
  config: StyleConfig,
): Promise<SaveDefaultsResult> {
  if (!isValidPresetName(presetName)) {
    return { ok: false, error: `invalid preset name: ${presetName}` };
  }
  const result = await saveDefaultsToSource(presetFilePath(presetName), config);
  if (result.ok) clearPresetOverlay(presetName);
  return result;
}

/** Create a new preset file. Optionally bind it to a track URL so the next
 *  production boot for that map loads the new preset. */
export async function createNewPreset(
  presetName: string,
  config: StyleConfig,
  options: { bindToTrack?: string } = {},
): Promise<SaveDefaultsResult> {
  if (!isValidPresetName(presetName)) {
    return { ok: false, error: `invalid preset name: ${presetName}` };
  }
  if (presetForName(presetName)) {
    return { ok: false, error: `preset "${presetName}" already exists — use Save instead` };
  }
  const write = await saveDefaultsToSource(presetFilePath(presetName), config);
  if (!write.ok) return write;
  if (options.bindToTrack) {
    const bind = await bindTrackToPreset(options.bindToTrack, presetName);
    if (!bind.ok) return bind;
  }
  return write;
}

export function dispatchStyleConfigChange(config: StyleConfig): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<StyleConfig>(STYLE_CONFIG_EVENT, { detail: config }));
}

export function listenToStyleConfigChange(handler: (config: StyleConfig) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const onChange = (event: Event): void => {
    handler((event as CustomEvent<StyleConfig>).detail);
  };
  window.addEventListener(STYLE_CONFIG_EVENT, onChange);
  return () => window.removeEventListener(STYLE_CONFIG_EVENT, onChange);
}

function mergeStyleConfig(base: StyleConfig, raw: unknown): StyleConfig {
  if (!raw || typeof raw !== 'object') return base;
  const source = raw as Partial<StyleConfig>;
  return {
    master: mergeMasterConfig(base.master, source.master),
    overrides: mergeOverrideMap(base.overrides, source.overrides),
    dayCycle: mergeDayCycleConfig(base.dayCycle, source.dayCycle, base.master),
    artDirection: mergeArtDirectionConfig(base.artDirection, source.artDirection),
    sky: mergeSkyConfig(base.sky, source.sky),
    sunRig: mergeSunRigConfig(base.sunRig, source.sunRig),
    clouds: mergeCloudConfig(base.clouds, source.clouds),
    asphalt: mergeAsphaltConfig(base.asphalt, source.asphalt),
    trackGlint: { ...base.trackGlint, ...objectOrEmpty(source.trackGlint) },
    edgeLines: { ...base.edgeLines, ...objectOrEmpty(source.edgeLines) },
    kerbs: { ...base.kerbs, ...objectOrEmpty(source.kerbs) },
    furniture: { ...base.furniture, ...objectOrEmpty(source.furniture) },
    fog: { ...base.fog, ...objectOrEmpty(source.fog) },
    ground: { ...base.ground, ...objectOrEmpty(source.ground) },
    sfx: mergeSfxConfig(base.sfx, source.sfx, source as Record<string, unknown>),
  };
}

function mergeArtDirectionConfig(
  base: StyleConfig['artDirection'],
  source: Partial<StyleConfig['artDirection']> | undefined,
): StyleConfig['artDirection'] {
  if (!source) return base;
  return {
    ...base,
    ...objectOrEmpty(source),
    valueComposition: {
      ...base.valueComposition,
      ...objectOrEmpty(source.valueComposition),
    },
  };
}

function mergeAsphaltConfig(
  base: StyleConfig['asphalt'],
  source: Partial<StyleConfig['asphalt']> | undefined,
): StyleConfig['asphalt'] {
  if (!source) return base;
  return {
    ...base,
    ...objectOrEmpty(source),
    cracks: {
      ...base.cracks,
      ...objectOrEmpty(source.cracks),
    },
  };
}

function mergeMasterConfig(
  base: MasterLightingConfig,
  source: MasterLightingConfig | undefined,
): MasterLightingConfig {
  if (!source) return base;
  return {
    ...base,
    ...objectOrEmpty(source),
    secondarySun: { ...base.secondarySun, ...objectOrEmpty(source.secondarySun) },
    atmosphere: { ...base.atmosphere, ...objectOrEmpty(source.atmosphere) },
  };
}

function mergeOverrideMap(
  base: StyleOverrideMap,
  source: StyleOverrideMap | undefined,
): StyleOverrideMap {
  if (!source || typeof source !== 'object') return base;
  const next: StyleOverrideMap = { ...base };
  for (const [path, override] of Object.entries(source)) {
    if (MASTER_SUN_DIRECTION_OVERRIDE_PATHS.has(path)) continue;
    if (!override || typeof override !== 'object') continue;
    next[path] = {
      enabled: Boolean((override as StyleOverride).enabled),
      value: (override as StyleOverride).value,
    };
  }
  return next;
}

function mergeDayCycleConfig(
  base: DayCycleConfig,
  source: DayCycleConfig | undefined,
  fallbackMaster: MasterLightingConfig,
): DayCycleConfig {
  if (!source) return base;
  return {
    ...base,
    ...objectOrEmpty(source),
    keyframes: Array.isArray(source.keyframes)
      ? source.keyframes.map((keyframe, index) => ({
          id: String(keyframe.id || `key-${index}`),
          name: String(keyframe.name || `Key ${index + 1}`),
          sunElevationDeg: Number.isFinite(keyframe.sunElevationDeg)
            ? keyframe.sunElevationDeg
            : base.keyframes[index]?.sunElevationDeg ?? 0,
          master: mergeMasterConfig(base.keyframes[index]?.master ?? base.keyframes[0]?.master ?? fallbackMaster, keyframe.master),
          overrides: mergeOverrideMap({}, keyframe.overrides),
        }))
      : base.keyframes,
  };
}

function objectOrEmpty<T extends object>(value: T | undefined): Partial<T> {
  return value && typeof value === 'object' ? value : {};
}

function mergeSkyConfig(
  base: StyleConfig['sky'],
  source: StyleConfig['sky'] | undefined,
): StyleConfig['sky'] {
  if (!source) return base;
  return {
    ...base,
    ...objectOrEmpty(source),
    sunDir: { ...base.sunDir, ...objectOrEmpty(source.sunDir) },
    secondarySunDir: { ...base.secondarySunDir, ...objectOrEmpty(source.secondarySunDir) },
    binary: { ...base.binary, ...objectOrEmpty(source.binary) },
  };
}

function mergeSunRigConfig(
  base: StyleConfig['sunRig'],
  source: StyleConfig['sunRig'] | undefined,
): StyleConfig['sunRig'] {
  if (!source) return base;
  return {
    ...base,
    ...objectOrEmpty(source),
    key: { ...base.key, ...objectOrEmpty(source.key) },
    fill: { ...base.fill, ...objectOrEmpty(source.fill) },
    hemisphere: { ...base.hemisphere, ...objectOrEmpty(source.hemisphere) },
    bounce: { ...base.bounce, ...objectOrEmpty(source.bounce) },
    rim: {
      ...base.rim,
      ...objectOrEmpty(source.rim),
      cameraSpaceDir: {
        ...base.rim.cameraSpaceDir,
        ...objectOrEmpty(source.rim?.cameraSpaceDir),
      },
    },
  };
}

function mergeCloudConfig(
  base: StyleConfig['clouds'],
  source: StyleConfig['clouds'] | undefined,
): StyleConfig['clouds'] {
  if (!source) return base;
  return {
    shading: {
      ...base.shading,
      ...objectOrEmpty(source.shading),
    },
    layers: {
      low: mergeCloudLayerConfig(base.layers.low, source.layers?.low),
      mid: mergeCloudLayerConfig(base.layers.mid, source.layers?.mid),
      high: mergeCloudLayerConfig(base.layers.high, source.layers?.high),
    },
  };
}

function mergeCloudLayerConfig(
  base: CloudLayerConfig,
  source: Partial<CloudLayerConfig> | undefined,
): CloudLayerConfig {
  if (!source) return base;
  const next = { ...base };
  for (const key of Object.keys(base) as Array<keyof CloudLayerConfig>) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      next[key] = source[key] as never;
    }
  }
  return next;
}

function mergeSfxConfig(
  base: StyleConfig['sfx'],
  source: StyleConfig['sfx'] | undefined,
  raw: Record<string, unknown>,
): StyleConfig['sfx'] {
  const legacyLook = objectOrEmpty(raw.postFX as Partial<{
    bloomThreshold: number;
    bloomRadius: number;
    bloomIntensity: number;
    toneMapping: ToneMappingChoice;
    exposure: number;
    colorGradeEnabled: boolean;
    saturation: number;
    contrast: number;
    gamma: number;
    lutUrl: string | null;
  }>);
  const legacyMotion = objectOrEmpty(raw.motionBlur as Partial<StyleConfig['sfx']['motion']>);
  const next: StyleConfig['sfx'] = {
    bloom: { ...base.bloom, ...objectOrEmpty(source?.bloom) },
    selectiveBloom: { ...base.selectiveBloom, ...objectOrEmpty(source?.selectiveBloom) },
    colorGrade: {
      ...base.colorGrade,
      ...objectOrEmpty(source?.colorGrade),
      lift: { ...base.colorGrade.lift, ...objectOrEmpty(source?.colorGrade?.lift) },
      gain: { ...base.colorGrade.gain, ...objectOrEmpty(source?.colorGrade?.gain) },
    },
    vignette: { ...base.vignette, ...objectOrEmpty(source?.vignette) },
    chroma: { ...base.chroma, ...objectOrEmpty(source?.chroma) },
    motion: { ...base.motion, ...objectOrEmpty(source?.motion) },
  };

  if (typeof legacyLook.bloomThreshold === 'number') next.bloom.threshold = legacyLook.bloomThreshold;
  if (typeof legacyLook.bloomRadius === 'number') next.bloom.radius = legacyLook.bloomRadius;
  if (typeof legacyLook.bloomIntensity === 'number') next.bloom.intensity = legacyLook.bloomIntensity;
  if (legacyLook.toneMapping) next.colorGrade.toneMapping = legacyLook.toneMapping;
  if (typeof legacyLook.exposure === 'number') next.colorGrade.exposure = legacyLook.exposure;
  if (typeof legacyLook.colorGradeEnabled === 'boolean') next.colorGrade.enabled = legacyLook.colorGradeEnabled;
  if (typeof legacyLook.saturation === 'number') next.colorGrade.saturation = legacyLook.saturation;
  if (typeof legacyLook.contrast === 'number') next.colorGrade.contrast = legacyLook.contrast;
  if (typeof legacyLook.gamma === 'number') next.colorGrade.gamma = legacyLook.gamma;
  if (legacyLook.lutUrl === null || typeof legacyLook.lutUrl === 'string') next.colorGrade.lutUrl = legacyLook.lutUrl;
  Object.assign(next.motion, legacyMotion);

  return next;
}
